import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { IMG } from "@/lib/images";
import { fadeUp, stagger, lineReveal } from "@/lib/motion";
import { scrollToId } from "@/components/SmoothScroll";
import { ArrowDown } from "lucide-react";

export const Hero = () => {
  const { t } = useLang();
  const sectionRef = useRef(null);

  // Signature parallax: background drifts + scales, foreground copy lifts & fades.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.16]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const titleLines = t.hero.title.split("\n");

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-forest"
    >
      {/* Background: still image with slow ambient Ken Burns drift + scroll parallax */}
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={IMG.hero}
          alt="Guest walking along a wooden dock beside a misty Norwegian lake surrounded by pine forest."
          className="w-full h-full object-cover hero-kenburns"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-[rgba(15,38,29,0.55)]" />
        <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[rgba(15,38,29,0.9)] to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-[70%] bg-gradient-to-t from-[rgba(15,38,29,0.95)] via-[rgba(15,38,29,0.7)] to-transparent" />
        <div className="absolute inset-0 grain" />
      </motion.div>

      {/* Content */}
      <motion.div
        className="relative z-10 min-h-[100svh] flex flex-col pt-32 sm:pt-36 md:pt-40 pb-16 sm:pb-20 md:pb-28"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        {/* Flexible spacer pushes the editorial block toward the lower half */}
        <div className="flex-1 min-h-[2rem] md:min-h-[6rem]" aria-hidden="true" />

        <div className="max-w-[1400px] mx-auto w-full px-6 md:px-12">
          <motion.p
            initial={{ opacity: 0, letterSpacing: "0.5em" }}
            animate={{ opacity: 1, letterSpacing: "0.24em" }}
            transition={{ duration: 1.2, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="overline text-bronze mb-5 md:mb-8 text-shadow-hero"
            data-testid="hero-eyebrow"
          >
            {t.hero.eyebrow}
          </motion.p>

          {/* Signature moment: masked line-by-line reveal */}
          <h1
            className="font-serif text-ivory text-4xl sm:text-6xl lg:text-[5.5rem] leading-[1.05] tracking-tight font-light max-w-5xl text-shadow-hero"
            data-testid="hero-title"
          >
            {titleLines.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  className="block"
                  variants={lineReveal}
                  custom={i}
                  initial="hidden"
                  animate="show"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="contents"
          >
            <motion.div
              variants={fadeUp}
              className="mt-6 md:mt-10"
              data-testid="hero-badge-wrap"
              style={{ transitionDelay: "0.6s" }}
            >
              <span
                data-testid="hero-badge"
                className="inline-flex items-center gap-2 overline border border-ivory/70 text-ivory px-4 py-2"
              >
                <span
                  aria-hidden="true"
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ background: "var(--yz-bronze)" }}
                />
                {t.hero.badge}
              </span>
            </motion.div>

            <motion.p
              variants={fadeUp}
              className="mt-5 md:mt-6 max-w-xl text-ivory text-base md:text-lg leading-relaxed text-shadow-hero"
              data-testid="hero-subtitle"
            >
              {t.hero.subtitle}
            </motion.p>

            {/* Two side-by-side CTAs — same shape, two colors */}
            <motion.div
              variants={fadeUp}
              className="mt-10 md:mt-14 flex flex-row gap-3 sm:gap-4 max-w-xl"
              data-testid="hero-cta-row"
            >
              <button
                onClick={() => scrollToId("waitlist")}
                className="flex-1 overline px-5 sm:px-8 py-4 sm:py-5 transition-all duration-300 bg-bronze text-ivory hover:bg-[var(--yz-copper)] hover:tracking-[0.28em]"
                style={{ background: "var(--yz-bronze)" }}
                data-testid="hero-cta-primary"
              >
                {t.hero.cta_primary}
              </button>
              <button
                onClick={() => scrollToId("investors")}
                className="flex-1 overline px-5 sm:px-8 py-4 sm:py-5 transition-all duration-300 border border-ivory/80 text-ivory hover:bg-ivory hover:text-forest hover:tracking-[0.28em]"
                data-testid="hero-cta-secondary"
              >
                {t.hero.cta_secondary}
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom strip */}
        <div className="max-w-[1400px] mx-auto w-full px-6 md:px-12 mt-10 md:mt-16 flex items-end justify-between text-ivory/85">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="overline max-w-xs hidden md:block"
            data-testid="hero-tagline"
          >
            {t.hero.tagline}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="flex items-center gap-3 overline scroll-cue ml-auto"
            data-testid="hero-scroll-cue"
          >
            <span>{t.hero.scroll}</span>
            <ArrowDown size={14} />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

